import { lazy, Suspense, useEffect, useLayoutEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useStore } from "./store";
import I18nDomTranslator from "./components/ui/I18nDomTranslator";
import ErrorBoundary from "./components/ErrorBoundary";

const AuthPage      = lazy(() => import("./pages/AuthPage"));
const MainLayout    = lazy(() => import("./pages/MainLayout"));
const ShareRedirect = lazy(() => import("./pages/ShareRedirect"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const InviteLanding = lazy(() => import("./pages/InviteLanding"));

function PageSpinner() {
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="w-9 h-9 rounded-full border-3 border-[#fde68a] border-t-gold-500 animate-spin" />
    </div>
  );
}

function RecoveryRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  useLayoutEffect(() => {
    if (location.pathname === "/reset-password") return;
    const hp = new URLSearchParams(location.hash ? location.hash.slice(1) : "");
    const sp = new URLSearchParams(location.search ? location.search.slice(1) : "");
    const isRecovery =
      hp.get("type") === "recovery" ||
      sp.get("type") === "recovery";
    if (!isRecovery) return;
    navigate(`/reset-password${location.search}${location.hash}`, {
      replace: true,
    });
  }, [
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);
  return null;
}

function LoginRoute({ isGuest }) {
  const location = useLocation();
  const requestedReturnTo = new URLSearchParams(location.search).get("returnTo");
  const returnTo = requestedReturnTo?.startsWith("/") && !requestedReturnTo.startsWith("//")
    ? requestedReturnTo
    : "/";
  return isGuest ? <AuthPage /> : <Navigate to={returnTo} replace />;
}

export default function App() {
  const authStatus      = useStore((s) => s.authStatus);
  const authInitialized = useStore((s) => s.authInitialized);
  const initAuth        = useStore((s) => s.initAuth);

  // Cmd+K / Ctrl+K → focus search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        document.getElementById("denbaya-search")?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Initialize auth on mount — sets authInitialized once session is known
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  // Show spinner until we know whether the user is logged in
  if (!authInitialized) return <PageSpinner />;

  const isGuest = authStatus === "guest";

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSpinner />}>
        <I18nDomTranslator />
        <RecoveryRedirect />
        <Routes>
          <Route
            path="/login"
            element={<LoginRoute isGuest={isGuest} />}
          />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invite/:token" element={<InviteLanding />} />
          <Route path="/share/:token" element={<ShareRedirect />} />
          <Route
            path="/*"
            element={isGuest ? <Navigate to="/login" replace /> : <MainLayout />}
          />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
